import { RECOVERY_KEY_BYTES } from './recovery.ts';
import {
  importPrivateKey,
  privateJwkFromSecretD,
  type IdentityBundle,
} from './identity.ts';
import { base64UrlToBytes } from './bytes.ts';

export const ADULT_IDENTITY_STORAGE_KEY = 'farfar-crypto-jwk';
export const ADULT_VAULT_CHANGED_EVENT = 'farfar-adult-vault-changed';

export class AdultVaultError extends Error {
  readonly code:
    | 'MISSING'
    | 'WRONG_USER'
    | 'EXTRACTABLE'
    | 'MISMATCH'
    | 'INVALID';

  constructor(
    code: AdultVaultError['code'],
    message: string
  ) {
    super(message);
    this.name = 'AdultVaultError';
    this.code = code;
  }
}

export interface AdultVaultIdentity {
  userId: string;
  privateKeyJwk: JsonWebKey;
  privateKey?: CryptoKey | null;
  secretD?: Uint8Array | null;
}

export interface AdultVaultAccess {
  exportKey(ownerUserId: string): Promise<Uint8Array>;
  installKey(ownerUserId: string, vaultKey: Uint8Array): Promise<void>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function padP256Scalar(secretD: Uint8Array): Uint8Array {
  if (secretD.length === RECOVERY_KEY_BYTES) return new Uint8Array(secretD);
  if (secretD.length === 0 || secretD.length > RECOVERY_KEY_BYTES) {
    throw new AdultVaultError(
      'INVALID',
      'Adult-chat identity is not a 32-byte P-256 scalar.'
    );
  }
  const padded = new Uint8Array(RECOVERY_KEY_BYTES);
  padded.set(secretD, RECOVERY_KEY_BYTES - secretD.length);
  return padded;
}

export function publicIdentityCoords(jwk: JsonWebKey | null | undefined): {
  kty: string;
  crv: string;
  x: string;
  y: string;
} | null {
  if (!jwk || jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y) {
    return null;
  }
  return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y };
}

export function publicIdentityMatches(
  left: JsonWebKey | null | undefined,
  right: JsonWebKey | null | undefined
): boolean {
  const a = publicIdentityCoords(left);
  const b = publicIdentityCoords(right);
  return Boolean(a && b && a.x === b.x && a.y === b.y);
}

function readLocal(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function readSession(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readDurableItem(key: string): string | null {
  return readLocal()?.getItem(key) ?? readSession()?.getItem(key) ?? null;
}

export function readStoredAdultIdentityJwk(userId: string): JsonWebKey | null {
  try {
    const raw = readDurableItem(ADULT_IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = asRecord(JSON.parse(raw));
    if (!parsed) return null;
    if (String(parsed.userId) !== String(userId)) return null;
    const jwk = asRecord(parsed.privateKeyJwk) as JsonWebKey | null;
    if (!jwk?.d) return null;
    return jwk;
  } catch {
    return null;
  }
}

export function persistAdultIdentityJwk(
  userId: string,
  privateKeyJwk: JsonWebKey,
  options?: { notify?: boolean }
): void {
  const local = readLocal();
  if (!local) {
    throw new AdultVaultError(
      'INVALID',
      'This browser cannot persist adult-chat keys in the durable store.'
    );
  }
  local.setItem(
    ADULT_IDENTITY_STORAGE_KEY,
    JSON.stringify({ userId: String(userId), privateKeyJwk })
  );
  readSession()?.removeItem(ADULT_IDENTITY_STORAGE_KEY);
  if (options?.notify !== false && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(ADULT_VAULT_CHANGED_EVENT, {
        detail: { userId: String(userId) },
      })
    );
  }
}

export function clearPersistedAdultIdentityJwk(): void {
  readLocal()?.removeItem(ADULT_IDENTITY_STORAGE_KEY);
  readSession()?.removeItem(ADULT_IDENTITY_STORAGE_KEY);
}

export function exportAdultVaultFromIdentity(input: {
  ownerUserId: string;
  expectedUserId: string;
  expectedPublicKeyJwk?: JsonWebKey | null;
  identity: AdultVaultIdentity | null | undefined;
}): Uint8Array {
  if (!input.expectedUserId) {
    throw new AdultVaultError(
      'MISSING',
      'Adult-chat keys cannot be wrapped until this account is signed in.'
    );
  }
  if (String(input.ownerUserId) !== String(input.expectedUserId)) {
    throw new AdultVaultError(
      'WRONG_USER',
      'Adult-chat keys on this browser belong to a different account.'
    );
  }
  const identity = input.identity;
  if (!identity?.privateKeyJwk) {
    throw new AdultVaultError(
      'MISSING',
      'Adult-chat keys are not on this browser. Unlock encryption before activating recovery. A dummy key will not be created.'
    );
  }
  if (String(identity.userId) !== String(input.expectedUserId)) {
    throw new AdultVaultError(
      'WRONG_USER',
      'Adult-chat keys on this browser belong to a different account.'
    );
  }
  if (!identity.privateKeyJwk.d) {
    if (identity.privateKey?.extractable === true) {
      throw new AdultVaultError(
        'EXTRACTABLE',
        'Refusing to export an extractable chat key that is not already in this account’s durable store.'
      );
    }
    throw new AdultVaultError(
      'MISSING',
      'Adult-chat keys are not on this browser. Unlock encryption before activating recovery. A dummy key will not be created.'
    );
  }
  if (!input.expectedPublicKeyJwk) {
    throw new AdultVaultError(
      'MISSING',
      'This account has no published adult-chat public key. Recovery will not wrap a replacement.'
    );
  }
  if (!publicIdentityMatches(identity.privateKeyJwk, input.expectedPublicKeyJwk)) {
    throw new AdultVaultError(
      'MISMATCH',
      'Local adult-chat identity does not match this account’s published public key.'
    );
  }

  const fromSecret = identity.secretD
    ? padP256Scalar(identity.secretD)
    : padP256Scalar(base64UrlToBytes(identity.privateKeyJwk.d));
  const fromJwk = padP256Scalar(base64UrlToBytes(identity.privateKeyJwk.d));
  if (
    fromSecret.length !== fromJwk.length ||
    !fromSecret.every((byte, index) => byte === fromJwk[index])
  ) {
    fromSecret.fill(0);
    fromJwk.fill(0);
    throw new AdultVaultError(
      'MISMATCH',
      'Local adult-chat identity bytes do not match the stored private key.'
    );
  }
  fromJwk.fill(0);
  return fromSecret;
}

export async function installAdultVaultKey(input: {
  ownerUserId: string;
  expectedUserId: string;
  expectedPublicKeyJwk?: JsonWebKey | null;
  vaultKey: Uint8Array;
}): Promise<Pick<IdentityBundle, 'privateKey' | 'privateKeyJwk' | 'secretD'>> {
  if (!input.expectedUserId) {
    throw new AdultVaultError(
      'MISSING',
      'Adult-chat keys cannot be installed until this account is signed in.'
    );
  }
  if (String(input.ownerUserId) !== String(input.expectedUserId)) {
    throw new AdultVaultError(
      'WRONG_USER',
      'Recovered adult-chat keys cannot be installed for a different account.'
    );
  }
  if (!input.expectedPublicKeyJwk) {
    throw new AdultVaultError(
      'MISSING',
      'This account has no published adult-chat public key to restore into.'
    );
  }
  const secretD = padP256Scalar(input.vaultKey);
  const privateKeyJwk = privateJwkFromSecretD(input.expectedPublicKeyJwk, secretD);
  let privateKey: CryptoKey;
  try {
    privateKey = await importPrivateKey(privateKeyJwk);
  } catch {
    secretD.fill(0);
    throw new AdultVaultError(
      'INVALID',
      'Recovered adult-chat key could not be imported on this device.'
    );
  }
  persistAdultIdentityJwk(input.expectedUserId, privateKeyJwk);
  return {
    privateKey,
    privateKeyJwk,
    secretD,
  };
}

export function createMemoryAdultVault(options: {
  userId: string;
  publicKeyJwk: JsonWebKey;
  secretD?: Uint8Array;
}): AdultVaultAccess {
  let secretD = options.secretD ? padP256Scalar(options.secretD) : null;
  return {
    async exportKey(ownerUserId) {
      if (!secretD) {
        throw new AdultVaultError(
          'MISSING',
          'Adult-chat keys are not on this browser. Unlock encryption before activating recovery. A dummy key will not be created.'
        );
      }
      return exportAdultVaultFromIdentity({
        ownerUserId,
        expectedUserId: options.userId,
        expectedPublicKeyJwk: options.publicKeyJwk,
        identity: {
          userId: options.userId,
          privateKeyJwk: privateJwkFromSecretD(options.publicKeyJwk, secretD),
          secretD,
        },
      });
    },
    async installKey(ownerUserId, vaultKey) {
      const installed = await installAdultVaultKey({
        ownerUserId,
        expectedUserId: options.userId,
        expectedPublicKeyJwk: options.publicKeyJwk,
        vaultKey,
      });
      secretD?.fill(0);
      secretD = installed.secretD;
    },
  };
}
